/**
 * Slider Block
 * Self-contained slider component that can be used in any section.
 * Children of the slider block become slides.
 */

import { ThemeBus } from './events-bus.js';
import { EVENTS } from './event-constants.js';

// Track initialized sliders to prevent duplicates
const initializedSliders = new WeakSet();

// Queue for sliders waiting for Swiper to load
const pendingInitializations = [];

// Store instances for resize handling
const sliderInstances = new Map();

// Breakpoint for mobile/desktop (matches Tailwind md:)
const MOBILE_BREAKPOINT = 768;

class SliderBlock {
  constructor(element) {
    this.element = element;
    this.swiper = null;
    this.swiperEl = null;
    this.contentEl = null;
    this.controlsEls = [];
    this.relocatedControls = [];
    this.originalSlides = null;
    this.isInitialized = false;
    this.cleanupInteractionState = null;

    // Prevent duplicate instance creation
    if (initializedSliders.has(element)) return;
    initializedSliders.add(element);

    // Store visibility setting
    this.visibility = this.element.dataset.sliderVisibility || 'both';

    // Store instance for resize handling
    sliderInstances.set(element, this);

    // Discover controls elements early so we can manage their visibility
    this.controlsEls = this.findControlsElements();

    // Check if should init based on visibility
    this.checkVisibilityAndInit();

    // Listen for resize
    this.handleResize = this.handleResize.bind(this);
    window.addEventListener('resize', this.handleResize);
  }

  findControlsElements() {
    const found = new Set();
    const blockId = this.element.dataset.blockId;
    const section = this.element.closest('[id^="shopify-section-"]');

    // Nested controls inside the slider block
    this.element.querySelectorAll('[data-slider-control]').forEach(el => found.add(el));

    // Section-level controls explicitly targeting this slider
    if (section && blockId) {
      section.querySelectorAll(`[data-slider-control][data-slider-id="${blockId}"]`).forEach(el => found.add(el));
    }

    // Auto-match unassigned section-level controls only when no nested/targeted
    // controls were found and this is the only slider in the section
    if (found.size === 0 && section) {
      const allSliders = section.querySelectorAll('[data-slider-block]');
      if (allSliders.length === 1) {
        section.querySelectorAll('[data-slider-control]:not([data-slider-id])').forEach(el => found.add(el));
      }
    }

    return [...found];
  }

  updateControlsVisibility() {
    if (!this.controlsEls?.length) return;

    const shouldHide = !this.isInitialized || (this.swiper?.isLocked ?? false);
    for (const el of this.controlsEls) {
      el.style.display = shouldHide ? 'none' : '';
    }
  }

  isClippedByAncestor(el) {
    const section = this.element.closest('[id^="shopify-section-"]');
    let ancestor = el.parentElement;
    while (ancestor && ancestor !== section && ancestor !== this.element) {
      const style = getComputedStyle(ancestor);
      const ov = style.overflow;
      if ((ov === 'hidden' || ov === 'clip') && ancestor.getBoundingClientRect().height < 1) {
        return true;
      }
      ancestor = ancestor.parentElement;
    }
    return false;
  }

  relocateClippedControls() {
    for (const el of this.controlsEls) {
      if (el.closest('[data-slider-block]') === this.element) continue;
      if (!this.isClippedByAncestor(el)) continue;

      this.relocatedControls.push({
        el,
        originalParent: el.parentElement,
        originalNextSibling: el.nextSibling,
      });
      this.element.appendChild(el);
    }
  }

  restoreRelocatedControls() {
    for (const entry of this.relocatedControls) {
      if (entry.originalNextSibling?.parentElement === entry.originalParent) {
        entry.originalParent.insertBefore(entry.el, entry.originalNextSibling);
      } else {
        entry.originalParent.appendChild(entry.el);
      }
    }
    this.relocatedControls = [];
  }

  /**
   * Check if slider should be active based on visibility setting and viewport
   */
  shouldBeActive() {
    const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
    
    switch (this.visibility) {
      case 'mobile':
        return isMobile;
      case 'desktop':
        return !isMobile;
      case 'both':
      default:
        return true;
    }
  }

  /**
   * Check visibility and init/destroy accordingly
   */
  checkVisibilityAndInit() {
    const shouldBeActive = this.shouldBeActive();

    if (shouldBeActive && !this.isInitialized) {
      this.init();
    } else if (!shouldBeActive && this.isInitialized) {
      this.destroy();
    }

    this.updateControlsVisibility();
  }

  /**
   * Handle window resize
   */
  handleResize() {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      this.checkVisibilityAndInit();
      this.syncPageMarginOffsets();
    }, 150);
  }

  getOffsetSource() {
    return this.element.dataset.offsetSource || 'manual';
  }

  isBreakoutMode() {
    return Boolean(this.contentEl?.classList.contains('slider-breakout'));
  }

  resolveCssLengthToPx(value, fallback = 0) {
    if (!value) return fallback;

    const trimmedValue = value.trim();
    const numericValue = Number.parseFloat(trimmedValue);
    if (!Number.isFinite(numericValue)) return fallback;

    // Unitless values in theme settings are treated as px.
    if (/^-?\d+(\.\d+)?$/.test(trimmedValue)) return numericValue;

    const probe = document.createElement('div');
    probe.style.position = 'absolute';
    probe.style.visibility = 'hidden';
    probe.style.pointerEvents = 'none';
    probe.style.width = trimmedValue;
    this.element.appendChild(probe);
    const resolvedWidth = probe.getBoundingClientRect().width;
    probe.remove();

    if (Number.isFinite(resolvedWidth) && resolvedWidth > 0) {
      return resolvedWidth;
    }

    return numericValue;
  }

  getNestedLayoutGutterOffset() {
    const computedStyles = getComputedStyle(this.element);
    const pageMargin = this.resolveCssLengthToPx(computedStyles.getPropertyValue('--page-margin'), 0);
    const viewportWidth = document.documentElement.clientWidth;
    const fallbackPageWidth = Math.max(viewportWidth, pageMargin * 2);
    const pageWidth = this.resolveCssLengthToPx(computedStyles.getPropertyValue('--page-width'), fallbackPageWidth);

    const maxContentWidth = Math.max(0, pageWidth - pageMargin * 2);
    const viewportConstrainedWidth = Math.max(0, viewportWidth - pageMargin * 2);
    const contentWidth = Math.min(maxContentWidth, viewportConstrainedWidth);
    const centeredGutter = (viewportWidth - contentWidth) / 2;
    const gutterOffset = Math.max(pageMargin, centeredGutter);

    if (Number.isFinite(gutterOffset) && gutterOffset >= 0) {
      return gutterOffset;
    }

    return 0;
  }

  getEffectiveSlidesPerView(options) {
    let spv = options?.slidesPerView;
    if (options?.breakpoints) {
      const vw = document.documentElement.clientWidth;
      for (const bp of Object.keys(options.breakpoints).map(Number).filter(Number.isFinite).sort((a, b) => a - b)) {
        if (vw >= bp && options.breakpoints[bp]?.slidesPerView != null) {
          spv = options.breakpoints[bp].slidesPerView;
        }
      }
    }
    return spv;
  }

  hasFractionalSlidesPerView(spv) {
    return typeof spv === 'number' && spv % 1 !== 0;
  }

  applyPageMarginOffsets(options) {
    if (this.getOffsetSource() !== 'page-margin') return options;

    const pageMarginOffset = this.getNestedLayoutGutterOffset();
    const withPageMarginOffsets = options;
    const useCssBreakoutGutter = this.isBreakoutMode();

    if (useCssBreakoutGutter) {
      const parseNum = (v, fb = 0) => { const n = Number.parseFloat(v); return Number.isFinite(n) ? n : fb; };
      const spv = this.getEffectiveSlidesPerView(withPageMarginOffsets);
      const isPeek = this.hasFractionalSlidesPerView(spv);
      const endGap = isPeek ? 0 : parseNum(withPageMarginOffsets.spaceBetween, 0);
      const viewportWidth = document.documentElement.clientWidth;
      const setVars = (el) => {
        if (!el) return;
        el.style.setProperty('--slider-breakout-gutter', `${pageMarginOffset}px`);
        el.style.setProperty('--slider-breakout-end-gap', `${endGap}px`);
        el.style.setProperty('--slider-breakout-width', `${viewportWidth}px`);
      };
      setVars(this.element);
      setVars(this.contentEl);
    }

    withPageMarginOffsets.slidesOffsetBefore = useCssBreakoutGutter ? 0 : pageMarginOffset;
    withPageMarginOffsets.slidesOffsetAfter = useCssBreakoutGutter ? 0 : pageMarginOffset;

    if (withPageMarginOffsets.breakpoints && typeof withPageMarginOffsets.breakpoints === 'object') {
      Object.values(withPageMarginOffsets.breakpoints).forEach((breakpointOptions) => {
        if (!breakpointOptions || typeof breakpointOptions !== 'object') return;
        breakpointOptions.slidesOffsetBefore = useCssBreakoutGutter ? 0 : pageMarginOffset;
        breakpointOptions.slidesOffsetAfter = useCssBreakoutGutter ? 0 : pageMarginOffset;
      });
    }

    return withPageMarginOffsets;
  }

  syncPageMarginOffsets() {
    if (!this.swiper || this.getOffsetSource() !== 'page-margin') return;

    const pageMarginOffset = this.getNestedLayoutGutterOffset();
    const useCssBreakoutGutter = this.isBreakoutMode();

    if (useCssBreakoutGutter) {
      const parseNum = (v, fb = 0) => { const n = Number.parseFloat(v); return Number.isFinite(n) ? n : fb; };
      const spv = this.swiper.params?.slidesPerView;
      const isPeek = this.hasFractionalSlidesPerView(spv);
      const endGap = isPeek ? 0 : parseNum(this.swiper.params?.spaceBetween, 0);
      const viewportWidth = document.documentElement.clientWidth;
      const setVars = (el) => {
        if (!el) return;
        el.style.setProperty('--slider-breakout-gutter', `${pageMarginOffset}px`);
        el.style.setProperty('--slider-breakout-end-gap', `${endGap}px`);
        el.style.setProperty('--slider-breakout-width', `${viewportWidth}px`);
      };
      setVars(this.element);
      setVars(this.contentEl);
    }

    const applyOffsets = (target) => {
      if (!target || typeof target !== 'object') return;
      target.slidesOffsetBefore = useCssBreakoutGutter ? 0 : pageMarginOffset;
      target.slidesOffsetAfter = useCssBreakoutGutter ? 0 : pageMarginOffset;
    };

    applyOffsets(this.swiper.params);
    applyOffsets(this.swiper.originalParams);

    if (this.swiper.params?.breakpoints && typeof this.swiper.params.breakpoints === 'object') {
      Object.values(this.swiper.params.breakpoints).forEach(applyOffsets);
    }

    if (this.swiper.originalParams?.breakpoints && typeof this.swiper.originalParams.breakpoints === 'object') {
      Object.values(this.swiper.originalParams.breakpoints).forEach(applyOffsets);
    }

    this.swiper.update();
  }

  /**
   * Initializes the Swiper slider
   */
  init() {
    const Swiper = window.Swiper;

    if (!Swiper) {
      pendingInitializations.push(this);
      return;
    }

    if (this.cleanupInteractionState) {
      this.cleanupInteractionState();
      this.cleanupInteractionState = null;
    }

    // Content container
    this.contentEl = this.element.querySelector('.slider-content');
    if (!this.contentEl) return;

    const blockId = this.element.dataset.blockId;

    // Extract any controls from contentEl BEFORE clearing it, so they're not destroyed
    for (const el of this.controlsEls) {
      if (el.parentElement === this.contentEl) {
        this.element.appendChild(el);
      }
    }

    // Move controls out of any zero-height overflow:hidden ancestors
    this.relocateClippedControls();

    // Use the first controls element for Swiper wiring (navigation, pagination)
    const controlsElement = this.controlsEls[0] || null;

    let slides;
    
    if (this.originalSlides) {
      slides = this.originalSlides;
    } else {
      // Direct children become slides (filter out style, script, controls, and other non-content elements)
      const excludedTags = ['STYLE', 'SCRIPT', 'LINK', 'META', 'NOSCRIPT', 'TEMPLATE'];
      slides = [...this.contentEl.children].filter(el => 
        !excludedTags.includes(el.tagName) && 
        !el.hasAttribute('data-slider-control')
      );
      
      if (slides.length < 1) {
        return;
      }

      this.originalSlides = slides;
    }

    // Create Swiper markup
    const swiperEl = document.createElement('div');
    swiperEl.classList.add('slider-block-swiper', 'w-full', 'max-w-full');
    
    if (this.element.dataset.overflowHidden !== 'false') {
      swiperEl.classList.add('overflow-x-hidden');
    }
    
    swiperEl.dataset.swiperInstance = this.element.dataset.blockId;
    this.swiperEl = swiperEl;

    const wrapperEl = document.createElement('div');
    wrapperEl.classList.add('swiper-wrapper');

    // Move children into Swiper wrapper as slides
    slides.forEach(slide => {
      slide.classList.add('swiper-slide');
      wrapperEl.appendChild(slide);
    });

    swiperEl.appendChild(wrapperEl);

    // Replace content with Swiper
    this.contentEl.innerHTML = '';
    this.contentEl.appendChild(swiperEl);

    // Get options from data attribute
    const customOptions = this.element.dataset.swiperOptions
      ? JSON.parse(this.element.dataset.swiperOptions) 
      : {};

    // Default options
    const defaultOptions = {
      slidesPerView: 1,
      spaceBetween: 16,
      // Enable touch/drag interactions
      allowTouchMove: true,
      simulateTouch: true,
      touchRatio: 1,
      touchAngle: 45,
      grabCursor: true,
      // Improve touch responsiveness
      shortSwipes: true,
      longSwipes: true,
      longSwipesRatio: 0.5,
      longSwipesMs: 300,
      followFinger: true,
      threshold: 5,
      touchMoveStopPropagation: false,
      touchStartPreventDefault: true,
      touchStartForcePreventDefault: true,
      noSwiping: false,
      touchReleaseOnEdges: false,
    };

    const customPaginationColour = this.element.dataset.paginationColour;

    const getEffectiveSlidesPerView = () => {
      let slidesPerView = customOptions.slidesPerView ?? defaultOptions.slidesPerView;

      if (customOptions.breakpoints && typeof customOptions.breakpoints === 'object') {
        let matchedBreakpoint = null;

        Object.keys(customOptions.breakpoints).forEach((breakpointKey) => {
          const breakpointValue = Number.parseInt(breakpointKey, 10);
          if (Number.isFinite(breakpointValue) && window.innerWidth >= breakpointValue) {
            if (matchedBreakpoint === null || breakpointValue > matchedBreakpoint) {
              matchedBreakpoint = breakpointValue;
            }
          }
        });

        if (matchedBreakpoint !== null) {
          const breakpointOptions = customOptions.breakpoints[String(matchedBreakpoint)];
          if (breakpointOptions && breakpointOptions.slidesPerView !== undefined) {
            slidesPerView = breakpointOptions.slidesPerView;
          }
        }
      }

      if (slidesPerView === 'auto') return null;

      const numericSlidesPerView = Number.parseFloat(slidesPerView);
      return Number.isFinite(numericSlidesPerView) && numericSlidesPerView > 0
        ? numericSlidesPerView
        : null;
    };

    // Only enable loop if more than 1 slide
    if (slides.length > 1 && customOptions.loop !== false) {
      defaultOptions.loop = true;
    }

    // Avoid loop when all slides are already fully visible at load.
    if (defaultOptions.loop) {
      const effectiveSlidesPerView = getEffectiveSlidesPerView();
      if (effectiveSlidesPerView !== null && slides.length <= effectiveSlidesPerView) {
        defaultOptions.loop = false;
      }
    }

    // Add navigation selectors (blockId already defined earlier)
    if (blockId && customOptions.navigation !== false) {
      let navSelector;
      
      if (controlsElement) {
        // Controls exist - use them for navigation
        if (controlsElement.dataset.sliderId === blockId) {
          // Controls are separate and explicitly linked
          navSelector = `[data-slider-control][data-slider-id="${blockId}"]`;
        } else if (!controlsElement.dataset.sliderId) {
          // Controls are nested or auto-matched, use the controls element directly
          // Don't use a selector string, pass the actual element's navigation buttons
          const nextBtn = controlsElement.querySelector('.slider-navigation-next');
          const prevBtn = controlsElement.querySelector('.slider-navigation-prev');
          if (nextBtn && prevBtn) {
            customOptions.navigation = {
              nextEl: nextBtn,
              prevEl: prevBtn,
            };
          }
        }
      }
      
      // If navSelector is set (separate controls), use selector strings
      if (navSelector) {
        customOptions.navigation = {
          nextEl: `${navSelector} .slider-navigation-next`,
          prevEl: `${navSelector} .slider-navigation-prev`,
        };
      }
    }

    // Check if slider-controls block is present (already found earlier)
    let controlsPaginationColour = customPaginationColour;
    
    if (controlsElement) {
      // Find pagination element
      const paginationEl = controlsElement.querySelector('.swiper-pagination');
      if (paginationEl) {
        // Get pagination type and colour from data attributes
        const paginationType = paginationEl.dataset.paginationType || 'bullets';
        controlsPaginationColour = paginationEl.dataset.paginationColour || customPaginationColour;
        
        // Override pagination config
        customOptions.pagination = {
          el: paginationEl,
          type: paginationType,
          clickable: true,
        };
        
        // Handle custom pagination type - render as styled bars
        if (paginationType === 'custom') {
          customOptions.pagination.renderCustom = (swiper, current, total) => {
            let bullets = '';
            for (let i = 1; i <= total; i++) {
              const isActive = i === current ? 'opacity-100' : 'opacity-50';
              bullets += `<span class="block w-10 h-1 rounded-full cursor-pointer transition-opacity duration-300 ${isActive}" style="background-color: ${controlsPaginationColour};" data-slide-index="${i}"></span>`;
            }
            return `<div class="flex gap-2 justify-center">${bullets}</div>`;
          };

          // Make custom bullets clickable
          this.element.addEventListener('click', (e) => {
            const bullet = e.target.closest('[data-slide-index]');
            if (bullet && this.swiper) {
              const index = parseInt(bullet.dataset.slideIndex, 10) - 1;
              this.swiper.slideTo(index);
            }
          });
        }
      }
    } else {
      // Fallback: Handle custom pagination type when no controls block (old behavior)
      if (customOptions.pagination && customOptions.pagination.type === 'custom') {
        customOptions.pagination.renderCustom = (swiper, current, total) => {
          let bullets = '';
          for (let i = 1; i <= total; i++) {
            const isActive = i === current ? 'opacity-100' : 'opacity-50';
            bullets += `<span class="block w-10 h-1 rounded-full cursor-pointer transition-opacity duration-300 ${isActive}" style="background-color: ${customPaginationColour};" data-slide-index="${i}"></span>`;
          }
          return `<div class="flex gap-2 justify-center">${bullets}</div>`;
        };

        // Make custom bullets clickable
        this.element.addEventListener('click', (e) => {
          const bullet = e.target.closest('[data-slide-index]');
          if (bullet && this.swiper) {
            const index = parseInt(bullet.dataset.slideIndex, 10) - 1;
            this.swiper.slideTo(index);
          }
        });
      }
    }

    this.applyPageMarginOffsets(customOptions);

    // Initialize Swiper
    this.swiper = new Swiper(swiperEl, {
      ...defaultOptions,
      ...customOptions,
    });

    this.syncPageMarginOffsets();
    this.setupNavigationInteractionState(controlsElement);
    this.isInitialized = true;
    this.updateControlsVisibility();

    this.swiper.on('lock', () => this.updateControlsVisibility());
    this.swiper.on('unlock', () => this.updateControlsVisibility());
  }

  setupNavigationInteractionState(controlsElement) {
    if (!controlsElement || !this.swiper) return;

    const navContainer = controlsElement.querySelector('.slider-nav-hover[data-slider-nav-state]');
    if (!navContainer || navContainer.dataset.sliderNavState !== 'initial') return;

    const nextBtn = controlsElement.querySelector('.slider-navigation-next');
    const prevBtn = controlsElement.querySelector('.slider-navigation-prev');
    if (!nextBtn || !prevBtn) return;

    const isLooping = Boolean(this.swiper.params?.loop);
    let hasInteracted = false;
    let isTrackingBoundaryState = false;

    const updateBoundaryState = () => {
      if (!hasInteracted || isLooping) return;

      navContainer.classList.toggle('slider-nav-at-start', this.swiper.isBeginning);
      navContainer.classList.toggle('slider-nav-at-end', this.swiper.isEnd);
    };

    const scheduleBoundaryStateUpdate = () => {
      requestAnimationFrame(updateBoundaryState);
    };

    const handleFirstInteraction = () => {
      if (hasInteracted) return;
      hasInteracted = true;

      navContainer.dataset.sliderNavState = 'interacted';
      navContainer.classList.remove('slider-nav-initial');
      navContainer.classList.add('slider-nav-interacted');

      if (isLooping) {
        navContainer.classList.remove('slider-nav-at-start');
        navContainer.classList.remove('slider-nav-at-end');
      } else {
        scheduleBoundaryStateUpdate();
        this.swiper.on('slideChange', scheduleBoundaryStateUpdate);
        this.swiper.on('reachBeginning', scheduleBoundaryStateUpdate);
        this.swiper.on('reachEnd', scheduleBoundaryStateUpdate);
        this.swiper.on('fromEdge', scheduleBoundaryStateUpdate);
        this.swiper.on('transitionEnd', scheduleBoundaryStateUpdate);
        isTrackingBoundaryState = true;
      }

      teardownFirstInteractionListeners();
    };

    const teardownFirstInteractionListeners = () => {
      nextBtn.removeEventListener('click', handleFirstInteraction);
      prevBtn.removeEventListener('click', handleFirstInteraction);
      navContainer.removeEventListener('focusin', handleFirstInteraction);
      this.swiper?.off?.('touchStart', handleFirstInteraction);
    };

    const teardownListeners = () => {
      teardownFirstInteractionListeners();
      if (isTrackingBoundaryState) {
        this.swiper?.off?.('slideChange', scheduleBoundaryStateUpdate);
        this.swiper?.off?.('reachBeginning', scheduleBoundaryStateUpdate);
        this.swiper?.off?.('reachEnd', scheduleBoundaryStateUpdate);
        this.swiper?.off?.('fromEdge', scheduleBoundaryStateUpdate);
        this.swiper?.off?.('transitionEnd', scheduleBoundaryStateUpdate);
        isTrackingBoundaryState = false;
      }
    };

    nextBtn.addEventListener('click', handleFirstInteraction);
    prevBtn.addEventListener('click', handleFirstInteraction);
    navContainer.addEventListener('focusin', handleFirstInteraction);
    this.swiper.on('touchStart', handleFirstInteraction);

    this.cleanupInteractionState = teardownListeners;
  }

  /**
   * Destroys the Swiper instance and restores original markup
   */
  destroy() {
    if (this.cleanupInteractionState) {
      this.cleanupInteractionState();
      this.cleanupInteractionState = null;
    }

    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    this.restoreRelocatedControls();

    if (this.swiperEl && this.originalSlides && this.contentEl) {
      this.contentEl.innerHTML = '';
      this.originalSlides.forEach(slide => {
        slide.classList.remove('swiper-slide');
        this.contentEl.appendChild(slide);
      });
    }

    this.swiperEl = null;
    this.isInitialized = false;
  }

  /**
   * Full cleanup including event listeners
   */
  cleanup() {
    window.removeEventListener('resize', this.handleResize);
    clearTimeout(this.resizeTimeout);
    this.destroy();
  }
}

/**
 * Initialize all slider blocks on the page
 */
function initSliderBlocks() {
  const sliders = document.querySelectorAll('[data-slider-block]');
  sliders.forEach(slider => {
    new SliderBlock(slider);
  });
}

/**
 * Process pending initializations once Swiper is available
 */
function processPendingInitializations() {
  if (!window.Swiper || pendingInitializations.length === 0) return;
  
  while (pendingInitializations.length > 0) {
    const instance = pendingInitializations.shift();
    instance.init();
  }
}

/**
 * Wait for Swiper to be available
 */
function waitForSwiper() {
  if (window.Swiper) {
    processPendingInitializations();
    return;
  }

  let _swiper = window.Swiper;
  Object.defineProperty(window, 'Swiper', {
    configurable: true,
    get() {
      return _swiper;
    },
    set(value) {
      _swiper = value;
      Object.defineProperty(window, 'Swiper', {
        configurable: true,
        writable: true,
        value: value
      });
      processPendingInitializations();
    }
  });
}

/**
 * Re-initialize sliders after dynamic content updates
 */
function handleGridUpdated() {
  initSliderBlocks();
  processPendingInitializations();
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initSliderBlocks();
    waitForSwiper();
  });
} else {
  initSliderBlocks();
  waitForSwiper();
}

// Re-initialize on back/forward navigation
window.addEventListener('pageshow', (e) => {
  if (e.persisted) {
    initSliderBlocks();
  }
});

// Re-initialize sliders when dynamic sections inject new markup
ThemeBus.addEventListener(EVENTS.gridUpdated, handleGridUpdated);

// Export for potential external use
window.SliderBlock = SliderBlock;
window.initSliderBlocks = initSliderBlocks;

